<template>
    <transition name="nebula-modal-transition" mode="out-in">
        <NebulaModal v-if="showCreateModal || showEditModal" class="create-request-template" @close-modal="cancelModal" size="m">
            <template v-slot:header>
                <h1 class="create-request-template__header">{{ $t('Open Request Template') }}</h1>
                <p class="create-request-template__details">{{ $t('This will be shown to educators to spark ideas and edit for their own use.') }}</p>
            </template>
            <template v-slot:content>
                <h3 class="create-request-template__subheader">{{ $t('Request Details') }}</h3>
                <ProfileFormField name="title" :inputGroupData="formData.title" :validation="validateInput" />
                <ProfileFormField name="description" inputType="textarea" :inputGroupData="formData.description" :validation="validateInput" />
                <ProfileFormField
                    name="primarySubject"
                    :inputGroupData="formData.primarySubject"
                    :validation="validateInput"
                    :selectOptions="tagOptions"
                    @updateSelect="handlePrimarySelected"
                />
                <ProfileFormField
                    inputType="multi-select"
                    name="subjects"
                    :key="formData.subjects.value.join(',')"
                    :inputGroupData="formData.subjects"
                    :validation="validateInput"
                    :selectOptions="additionalSubjectOptions"
                    :helperText="$t('These additional subjects will support in best matching to employees')"
                    :reduce="(opt) => opt.value"
                    @updateMultiSelect="handleMultiSelect"
                />
                <NebulaFieldGroup inputId="example-5" :labelText="formData.isFeatured.display">
                    <template v-slot:formElement>
                        <NebulaSwitch :isCompressed>
                            <template v-slot:checkbox>
                                <input
                                    class="nebula-switch__input"
                                    id="example-5"
                                    @input="toggleFeatured"
                                    :checked="formData.isFeatured.value"
                                    name="isFeatured"
                                    role="switch"
                                    type="checkbox"
                                />
                            </template>
                        </NebulaSwitch>
                    </template>
                </NebulaFieldGroup>
            </template>
            <template v-slot:actionsRow>
                <NebulaButton @click="cancelModal" type="flat" :text="$t('Cancel')" data-click-name="Cancel" />
                <NebulaButton
                    :customStyles="{
                        buttonTextColor: '#b41818',
                        buttonBorderColor: '#b41818',
                        buttonBackgroundHover: '#b41818',
                        buttonBorderColorHover: '#b41818',
                    }"
                    v-if="isEdit"
                    :isDisabled="loadingDelete"
                    type="ghost"
                    :text="$t('Delete')"
                    @click="deleteTemplate"
                    data-click-name="Delete"
                />
                <NebulaButton
                    :isDisabled="loadingSubmit"
                    :text="submitButtonText"
                    @click="confirmSubmit"
                    :data-click-type="submitClickType"
                    data-click-name="Submit Request Template"
                />
            </template>
        </NebulaModal>
    </transition>
</template>

<script>
import { NebulaModal, NebulaButton, NebulaFieldGroup, NebulaSwitch } from '@discoveryedu/nebula-components';
import ProfileFormField from '@/components/shared/Profile/ProfileFormField.vue';
import validation from '@/mixins/form/validation';
import createPayload from '@/mixins/data/createPayload';
import { tagOptions } from '@/data/formData';
import { useMainStore } from '@/stores/useMainStore';
import { useRequestTemplateStore } from '@/stores/useRequestTemplateStore';
import { useModalStore } from '@/stores/useModalStore';

export default {
    setup() {
        const store = useMainStore();
        const templateStore = useRequestTemplateStore();
        const modalStore = useModalStore();
        return { store, templateStore, modalStore };
    },
    name: 'RequestTemplatesModals',
    components: {
        NebulaModal,
        NebulaButton,
        NebulaFieldGroup,
        NebulaSwitch,
        ProfileFormField,
    },
    mixins: [validation, createPayload],
    computed: {
        showCreateModal() {
            return this.modalStore.createRequestTemplateModal.show;
        },
        showEditModal() {
            return this.modalStore.editRequestTemplateModal.show;
        },
        isEdit() {
            return this.modalStore.modals.requestTemplate.edit.show;
        },
        currentTemplate() {
            return this.modalStore.modals.requestTemplate.edit.template || {};
        },
        tagOptions() {
            return tagOptions().map((o) => ({ ...o, label: o.text }));
        },
        submitButtonText() {
            if (this.loadingSubmit) return this.$t('Processing');
            return this.isEdit ? this.$t('Edit Request Template') : this.$t('Add Request Template');
        },
        submitClickType() {
            return this.isEdit ? this.$t('Edit Request Template: DE Admin') : this.$t('Add Request Template: DE Admin');
        },
    },
    data() {
        return {
            loadingSubmit: false,
            loadingDelete: false,
            additionalSubjectOptions: [],
            formData: {
                title: {
                    value: '',
                    display: 'Title',
                    required: true,
                    error: null,
                    status: '',
                    placeholder: this.$t('Enter your request title here'),
                    requiredError: this.$t('Enter a title for your template'),
                },
                description: {
                    value: '',
                    display: 'Description',
                    inputType: 'textarea',
                    required: true,
                    error: null,
                    status: '',
                    placeholder: this.$t(
                        'Write a description for your request here. What topics would you like covered? What kind of professional experience are you looking for?',
                    ),
                    requiredError: this.$t('Add a description'),
                },
                primarySubject: {
                    value: '',
                    display: 'Primary Subject',
                    required: true,
                    error: null,
                    status: '',
                },
                subjects: {
                    value: [],
                    display: 'Additional Subject Area(s)',
                    required: false,
                    error: null,
                    status: '',
                },
                isFeatured: {
                    value: false,
                    display: this.$t('Would you like this template featured on the Homepage?'),
                    required: false,
                    error: null,
                },
            },
        };
    },
    methods: {
        togglecreateModal() {
            this.modalStore.toggleModal({
                category: 'requestTemplate',
                type: 'create',
                show: !this.modalStore.createRequestTemplateModal.show,
            });
        },
        cancelModal() {
            if (this.showEditModal) {
                this.modalStore.toggleModal({
                    category: 'requestTemplate',
                    type: 'edit',
                    show: false,
                });
            } else if (this.showCreateModal) {
                this.modalStore.toggleModal({
                    category: 'requestTemplate',
                    type: 'create',
                    show: false,
                });
            }
        },
        handlePrimarySelected() {
            const { value } = this.formData.primarySubject;
            this.additionalSubjectOptions = this.tagOptions.filter((o) => o.value !== value);
            const idx = this.formData.subjects.value.indexOf(value);
            if (idx >= 0) this.formData.subjects.value.splice(idx, 1);
        },
        handleMultiSelect({ name, values }) {
            this.formData[name].value = values;
        },
        setFormDataFromTemplate() {
            const template = this.currentTemplate;
            if (template && Object.keys(template).length > 0) {
                this.formData.title.value = template.title || '';
                this.formData.description.value = template.description || '';
                this.formData.primarySubject.value = template.primarySubject || '';
                this.formData.subjects.value = template.subjects ? [...template.subjects] : [];
                this.formData.isFeatured.value = template.isFeatured === true;
                this.additionalSubjectOptions = this.tagOptions.filter((o) => o.value !== template.primarySubject);
            }
        },
        resetFormData() {
            this.formData.title.value = '';
            this.formData.description.value = '';
            this.formData.primarySubject.value = '';
            this.formData.subjects.value = [];
            this.formData.isFeatured.value = false;
            this.additionalSubjectOptions = this.tagOptions;
        },
        async confirmSubmit() {
            await this.handleSaveAndContinue(true);
            if (this.fieldsWithErrors && this.fieldsWithErrors.length > 0) {
                return;
            }
            const queryParams = new URLSearchParams(this.store.page.pagination || {}).toString();
            const toSend = this.validationResults;
            const formattedSubmission = this.getTemplateToSend(toSend);
            const form = {
                templateId: this.currentTemplate.id,
                templateData: formattedSubmission,
                isFeatured: this.formData.isFeatured.value,
            };
            const payload = await this.createPayload(form);
            this.loadingSubmit = true;
            try {
                if (this.isEdit) {
                    await this.templateStore.updateRequestTemplate(payload);
                } else {
                    await this.templateStore.createRequestTemplate(payload);
                }

                const refetchPayload = await this.createPayload({ params: queryParams });
                await this.templateStore.getRequestTemplates(refetchPayload);

                this.cancelModal();
            } catch (error) {
                console.error(error);
            } finally {
                this.loadingSubmit = false;
            }
        },
        getTemplateToSend(formData) {
            const { title, description, primarySubject, subjects } = formData;

            return {
                title,
                description,
                primarySubject,
                subjects,
            };
        },
        async deleteTemplate() {
            const queryParams = new URLSearchParams(this.store.page.pagination || {}).toString();
            const form = {
                templateId: this.currentTemplate.id,
            };
            const payload = await this.createPayload(form);
            this.loadingDelete = true;
            try {
                await this.templateStore.deleteRequestTemplate(payload);
                const refetchPayload = await this.createPayload({ params: queryParams });
                await this.templateStore.getRequestTemplates(refetchPayload);
                this.cancelModal();
            } catch (error) {
                console.error(error);
            } finally {
                this.loadingDelete = false;
                this.cancelModal();
            }
        },
        toggleFeatured(event) {
            const isChecked = event.target.checked;
            const fieldName = event.target.name;

            if (fieldName === 'isFeatured') {
                this.formData.isFeatured.value = isChecked;
            }
        },
    },
    mounted() {
        this.additionalSubjectOptions = this.tagOptions;
    },
    watch: {
        showEditModal(show) {
            if (show) {
                this.setFormDataFromTemplate();
            }
        },
        showCreateModal(show) {
            if (show) {
                this.resetFormData();
            }
        },
    },
};
</script>

<style lang="stylus">
.create-request-template {
    --nebula-modal-background-color: $nebula-color-platform-interactive-100;

    &__header {
        margin-block-start: 0px;
        padding-top: $nebula-space-half;
        color: var(--Platform-Neutral-900, #3D3D3D);
        font-size: 35px;
        font-style: normal;
        font-weight: 700;
        line-height: 120%;
    }

    &__details {
        margin-block-start: $nebula-space-2x;
        margin-bottom: $nebula-space-2x;
    }

    &__subheader {
        margin-block-start: $nebula-space-6x;
        margin-bottom: $nebula-space-3x;
    }

    .nebula-modal {
        .nebula-modal__inner {
            padding-top: $nebula-space-4x;
            padding-right: $nebula-space-4x;
            padding-bottom: $nebula-space-4x;
            padding-left: $nebula-space-4x;
        }

    }

    .nebula-modal__scroll-container--m {
        max-width: 700px;
        max-height: 98vh;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }

    .profile-form-field textarea {
        max-width: 100%;
    }

    .nebula-field-group {
        max-width: 100%;
    }

    .vs__dropdown-menu {
        max-height: 140px;
        overflow-y: auto;
    }
}
</style>
